"use client";

import { useLazyQuery } from "@apollo/client";
import { AVATAR_COLORS, DEFAULT_AVATAR } from "@constants/global";
import Avatar from "boring-avatars";
import AvatarWithInitials from "components/core/AvatarWithInitials/AvatarWithInitials";
import { gqlUser } from "gql";
import {
  Award,
  CalendarDays,
  Coins,
  ExternalLink,
  Mail,
  MapPin,
  Users,
} from "lucide-react";
import moment from "moment";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import * as React from "react";
import { createPortal } from "react-dom";
import { IUser } from "types/User.types";
import { LiquidPill } from "./liquid-pill";
import { Z_INDEX } from "./z-index";
import { cn } from "@/lib/utils";

export interface UserTooltipProps {
  user?: Partial<IUser> | null;
  children: React.ReactNode;
  side?: "top" | "bottom";
  triggerClassName?: string;
}

const OPEN_DELAY_MS = 500;
const CLOSE_DELAY_MS = 120;
const PANEL_WIDTH = 340;
const PANEL_GAP = 8;
const AVATAR_SIZE = 56;

const getFullName = (user?: Partial<IUser> | null) =>
  [user?.name, user?.lastname].filter(Boolean).join(" ").trim();

const getInitials = (name: string) => name || "Usuario";

function InlineMeta({ icon, text }: { icon: React.ReactNode; text?: string | null }) {
  if (!text) return null;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-slate-600 dark:text-zinc-300">
      <span className="shrink-0 text-slate-400 dark:text-zinc-500">{icon}</span>
      <span className="truncate">{text}</span>
    </span>
  );
}

function StatBadge({
  icon,
  label,
  tone,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  tone: "indigo" | "amber";
}) {
  const toneClass =
    tone === "indigo"
      ? "bg-indigo-50 text-indigo-600 ring-indigo-100 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-500/25"
      : "bg-amber-50 text-amber-700 ring-amber-100 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/25";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold ring-1 ring-inset ${toneClass}`}
    >
      <span className="shrink-0">{icon}</span>
      {label}
    </span>
  );
}

export function UserTooltip({
  user: baseUser,
  children,
  side = "bottom",
  triggerClassName,
}: UserTooltipProps) {
  const { t } = useTranslation("common");
  const [mounted, setMounted] = React.useState(false);
  const [isTouch, setIsTouch] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const triggerRef = React.useRef<HTMLSpanElement | null>(null);
  const openTimerRef = React.useRef<number | null>(null);
  const closeTimerRef = React.useRef<number | null>(null);

  const [fetchProfile, { data: fetchedProfile }] = useLazyQuery(
    gqlUser.queries.GET_USER_TOOLTIP_PROFILE,
    { fetchPolicy: "cache-first" }
  );

  // Combina lo que ya tenemos (previewUser) con lo que llega on-demand del server.
  const user = React.useMemo<Partial<IUser> | null | undefined>(() => {
    if (!baseUser) return baseUser;
    return {
      ...baseUser,
      ...(fetchedProfile?.userProfile ?? {}),
    };
  }, [baseUser, fetchedProfile]);

  const name = getFullName(user);
  const profileHref = user?.id ? `/profile/${user.id}` : null;
  const roleName = user?.role?.name;
  const activeSince = user?.activeSince ? moment(user.activeSince) : null;
  const activeSinceLabel = activeSince?.isValid()
    ? activeSince.format("MMM YYYY")
    : null;

  const areaName = user?.area?.name;
  const teamName = user?.teams?.[0]?.name;
  const levelValue = user?.userLevel?.level;
  const coinsValue =
    typeof user?.coins === "number" && user.coins > 0 ? user.coins : null;

  React.useEffect(() => setMounted(true), []);

  // En dispositivos touch (pointer: coarse) el tooltip queda inhibido:
  // el hover no es fiable y el tap ya navega al perfil via <Link>. Ver
  // discussion en el rewrite de UserTooltip (2026-07). Detectamos con
  // matchMedia; si el usuario cambia de input (ej: tablet + teclado) el
  // listener actualiza.
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(pointer: coarse)");
    const sync = () => setIsTouch(mql.matches);
    sync();
    if (mql.addEventListener) {
      mql.addEventListener("change", sync);
      return () => mql.removeEventListener("change", sync);
    }
    // Safari <14 fallback.
    mql.addListener(sync);
    return () => mql.removeListener(sync);
  }, []);

  const clearTimers = React.useCallback(() => {
    if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    openTimerRef.current = null;
    closeTimerRef.current = null;
  }, []);

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  const openPanel = React.useCallback(() => {
    if (!baseUser?.id) return;
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);

    openTimerRef.current = window.setTimeout(() => {
      setRect(triggerRef.current?.getBoundingClientRect() ?? null);
      setOpen(true);
      fetchProfile({ variables: { userId: baseUser.id } });
    }, OPEN_DELAY_MS);
  }, [baseUser?.id, fetchProfile]);

  const closePanel = React.useCallback(() => {
    if (openTimerRef.current) window.clearTimeout(openTimerRef.current);

    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }, []);

  const keepOpen = React.useCallback(() => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
  }, []);

  React.useEffect(() => {
    if (!open || typeof window === "undefined") return;

    const update = () => setRect(triggerRef.current?.getBoundingClientRect() ?? null);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const getPanelStyle = React.useCallback(
    (triggerRect: DOMRect): React.CSSProperties => {
      const viewportWidth = typeof window === "undefined" ? 1280 : window.innerWidth;
      // Alineado al inicio del trigger (no centrado) para que el tooltip
      // aparezca justo donde el usuario está mirando, no desplazado.
      const left = Math.min(
        Math.max(10, triggerRect.left),
        viewportWidth - PANEL_WIDTH - 10
      );

      const top =
        side === "top"
          ? triggerRect.top - PANEL_GAP
          : triggerRect.bottom + PANEL_GAP;

      // Punto de origen relativo a la card: donde nace la gota líquida
      // (centro del trigger, dentro del panel).
      const triggerCenterX = triggerRect.left + triggerRect.width / 2;
      const originXPercent = Math.min(
        Math.max(6, ((triggerCenterX - left) / PANEL_WIDTH) * 100),
        94
      );
      const originYPercent = side === "top" ? 100 : 0;

      return {
        position: "fixed",
        left,
        top,
        width: PANEL_WIDTH,
        transform: side === "top" ? "translateY(-100%)" : undefined,
        zIndex: Z_INDEX.TOOLTIP,
        "--ut-origin-x": `${originXPercent}%`,
        "--ut-origin-y": `${originYPercent}%`,
      } as React.CSSProperties;
    },
    [side]
  );

  const avatar =
    !user?.profileImage || user.profileImage === DEFAULT_AVATAR ? (
      <Avatar size={AVATAR_SIZE} name={getInitials(name)} variant="beam" colors={AVATAR_COLORS} />
    ) : (
      <AvatarWithInitials
        urlImg={user.profileImage}
        initials={getInitials(name)}
        customSize={AVATAR_SIZE}
      />
    );

  const panel = open && rect && mounted && typeof document !== "undefined" ? createPortal(
    <div
      role="tooltip"
      onMouseEnter={keepOpen}
      onMouseLeave={closePanel}
      onClick={(event) => event.stopPropagation()}
      className="user-tooltip-root"
      style={getPanelStyle(rect)}
    >
      <style jsx global>{`
        /*
         * UserTooltip — Zoom-from-origin.
         *
         * El panel nace comprimido en el punto exacto del hover y se expande
         * hacia sus bordes finales usando transform-origin dinámico. Es un
         * movimiento físico (zoom), no una revelación por máscara. Al usar
         * solo transform + opacity + blur, la sombra nativa del LiquidPill
         * queda intacta (los transforms no clipean box-shadow).
         */
        @keyframes ut-zoom-in {
          0% {
            opacity: 0;
            transform: scale(0);
            filter: blur(10px);
          }
          8% {
            opacity: 1;
          }
          20% {
            filter: blur(5px);
          }
          45% {
            filter: blur(1.5px);
          }
          65% {
            transform: scale(1.045);
            filter: blur(0);
          }
          84% {
            transform: scale(0.984);
          }
          100% {
            opacity: 1;
            transform: scale(1);
            filter: blur(0);
          }
        }

        @keyframes ut-content-in {
          0%, 32% {
            opacity: 0;
            transform: translateY(3px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .user-tooltip-root {
          --ut-origin-x: 50%;
          --ut-origin-y: 0%;
          filter:
            drop-shadow(0 12px 26px rgba(15, 23, 42, 0.16))
            drop-shadow(0 4px 10px rgba(15, 23, 42, 0.10))
            drop-shadow(0 1px 2px rgba(15, 23, 42, 0.06));
        }

        .user-tooltip-card {
          display: flex;
          width: 100%;
          animation: ut-zoom-in 680ms cubic-bezier(0.22, 1.2, 0.36, 1) backwards;
          transform-origin: var(--ut-origin-x) var(--ut-origin-y);
          will-change: transform, opacity, filter;
        }

        .user-tooltip-card > * {
          width: 100%;
          min-width: 0;
        }

        .user-tooltip-content {
          animation: ut-content-in 520ms cubic-bezier(0.22, 0.95, 0.24, 1) both;
        }

        @media (prefers-reduced-motion: reduce) {
          .user-tooltip-card,
          .user-tooltip-content {
            animation: none !important;
          }
        }
      `}</style>

      <div className="user-tooltip-card">
        <LiquidPill
          as="div"
          shape="rounded"
          size="lg"
          surfaceStyle="solidTranslucent"
          shadow="default"
          reactive
        >
        <div className="user-tooltip-content flex w-full flex-col gap-3">
          {/* Header: avatar + nombre/rol */}
          <div className="flex items-center gap-3">
            <div className="shrink-0 rounded-full bg-white/90 p-0.5 shadow-[0_8px_22px_-16px_rgba(15,23,42,0.55)] ring-1 ring-white/95 dark:bg-zinc-900 dark:ring-white/10">
              {avatar}
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-bold leading-snug text-slate-950 dark:text-white">
                {name || t("Usuario")}
              </p>
              {roleName && (
                <p className="mt-0.5 truncate text-[11.5px] font-medium leading-tight text-slate-500 dark:text-zinc-400">
                  {roleName}
                </p>
              )}
            </div>

            {profileHref && (
              <Link
                href={profileHref}
                onClick={(event) => event.stopPropagation()}
                className="shrink-0"
                aria-label={t("Ver perfil") || "Ver perfil"}
              >
                <LiquidPill
                  as="span"
                  variant="indigo"
                  appearance="tinted"
                  shape="circle"
                  size="sm"
                  shadow="neutral"
                  icon={<ExternalLink className="h-3.5 w-3.5" />}
                  interactive
                  hoverBounce
                  hoverDeform
                />
              </Link>
            )}
          </div>

          {/* Meta list */}
          {(user?.email || user?.country || activeSinceLabel || teamName || areaName) && (
            <div className="space-y-1.5 border-t border-slate-200/60 pt-3 text-[11px] leading-snug dark:border-white/10">
              {user?.email && (
                <InlineMeta
                  icon={<Mail className="h-3 w-3" />}
                  text={user.email}
                />
              )}

              {(user?.country || activeSinceLabel) && (
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                  <InlineMeta
                    icon={<MapPin className="h-3 w-3" />}
                    text={user?.country}
                  />
                  <InlineMeta
                    icon={<CalendarDays className="h-3 w-3" />}
                    text={
                      activeSinceLabel
                        ? `${t("Desde")} ${activeSinceLabel}`
                        : null
                    }
                  />
                </div>
              )}

              {(teamName || areaName) && (
                <InlineMeta
                  icon={<Users className="h-3 w-3" />}
                  text={
                    teamName && areaName && teamName !== areaName
                      ? `${teamName} · ${areaName}`
                      : teamName || areaName
                  }
                />
              )}
            </div>
          )}

          {/* Footer: stats */}
          {(typeof levelValue === "number" || coinsValue != null) && (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-200/60 pt-3 dark:border-white/10">
              {typeof levelValue === "number" && (
                <StatBadge
                  tone="indigo"
                  icon={<Award className="h-3 w-3" />}
                  label={`Nv ${levelValue}`}
                />
              )}
              {coinsValue != null && (
                <StatBadge
                  tone="amber"
                  icon={<Coins className="h-3 w-3" />}
                  label={coinsValue.toLocaleString()}
                />
              )}
            </div>
          )}
        </div>
        </LiquidPill>
      </div>
    </div>,
    document.body
  ) : null;

  // En touch: no envolvemos con listeners — dejamos que el trigger
  // (típicamente un <Link>) se comporte de forma nativa y navegue al perfil
  // con un tap directo, sin interponer el tooltip.
  if (isTouch) {
    return <>{children}</>;
  }

  return (
    <span
      ref={triggerRef}
      className={cn("inline-flex", triggerClassName ?? "min-w-0")}
      onMouseEnter={openPanel}
      onMouseLeave={closePanel}
      onFocus={openPanel}
      onBlur={closePanel}
    >
      {children}
      {panel}
    </span>
  );
}
